import { useState, useMemo, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Link } from 'react-router-dom'
import { Hash, ArrowRight, Search, X } from 'lucide-react'
import { ScrollReveal } from '@/components/shared/ScrollReveal'
import { Badge } from '@/components/ui/Badge'
import { blogPosts, allHashtags, loadBlogSearch, type BlogPost } from '@/content/blogs'

interface MonthGroup {
  key: string
  label: string
  shortLabel: string
  year: number
  month: number
  count: number
}

function formatDate(dateStr: string, lang: 'en' | 'vi'): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(lang === 'vi' ? 'vi-VN' : 'en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  })
}

function getMonthLabel(year: number, month: number): { full: string; short: string } {
  const date = new Date(year, month, 1)
  return {
    full: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    short: date.toLocaleDateString('en-US', { month: 'short' })
  }
}

function groupPostsByMonth(posts: BlogPost[]): MonthGroup[] {
  const groups = new Map<string, MonthGroup>()
  
  posts.forEach(post => {
    const date = new Date(post.date)
    const year = date.getFullYear()
    const month = date.getMonth()
    const key = `${year}-${String(month).padStart(2, '0')}`
    
    if (groups.has(key)) {
      groups.get(key)!.count++
    } else {
      const labels = getMonthLabel(year, month)
      groups.set(key, {
        key,
        label: labels.full,
        shortLabel: labels.short,
        year,
        month,
        count: 1
      })
    }
  })
  
  return Array.from(groups.values()).sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year
    return b.month - a.month
  })
}

function getTitle(post: BlogPost): string {
  if (post.lang === 'en') {
    return post.titleEn
  }
  return post.firstLineVi.replace(/^#+\s*/, '').slice(0, 100)
}

function getExcerpt(post: BlogPost): string {
  if (post.lang === 'en') {
    return post.excerptEn
  }
  return post.excerptVi
}

export function BlogList() {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true })
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedHashtag, setSelectedHashtag] = useState<string | null>(null)
  const [selectedLang, setSelectedLang] = useState<'all' | 'en' | 'vi'>('all')
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null)
  const [visibleCount, setVisibleCount] = useState(12)
  const [searchIndex, setSearchIndex] = useState<Record<string, string> | null>(null)
  const [searchError, setSearchError] = useState(false)

  useEffect(() => {
    if (!searchQuery || searchIndex) return
    const controller = new AbortController()
    setSearchError(false)
    loadBlogSearch(controller.signal).then(setSearchIndex).catch(() => {
      if (!controller.signal.aborted) setSearchError(true)
    })
    return () => controller.abort()
  }, [searchQuery, searchIndex])

  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const content = searchIndex?.[post.id] ?? ''
      const title = getTitle(post)
      
      const matchesSearch = searchQuery === '' || 
        title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        content.toLowerCase().includes(searchQuery.toLowerCase())
      
      const matchesHashtag = !selectedHashtag || 
        post.hashtags.includes(selectedHashtag)
      
      const matchesLang = selectedLang === 'all' || post.lang === selectedLang
      
      const matchesMonth = !selectedMonth || (() => {
        const date = new Date(post.date)
        const postKey = `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`
        return postKey === selectedMonth
      })()
      
      return matchesSearch && matchesHashtag && matchesLang && matchesMonth
    })
  }, [searchQuery, selectedHashtag, selectedLang, selectedMonth, searchIndex])

  const monthGroups = useMemo(() => groupPostsByMonth(blogPosts), [])
  
  const groupedByYear = useMemo(() => {
    const yearMap = new Map<number, MonthGroup[]>()
    monthGroups.forEach(group => {
      if (!yearMap.has(group.year)) {
        yearMap.set(group.year, [])
      }
      yearMap.get(group.year)!.push(group)
    })
    return Array.from(yearMap.entries()).sort((a, b) => b[0] - a[0])
  }, [monthGroups])

  const visiblePosts = filteredPosts.slice(0, visibleCount)
  const hasMore = visibleCount < filteredPosts.length

  const clearFilters = () => {
    setSelectedMonth(null)
    setSearchQuery('')
    setSelectedHashtag(null)
    setSelectedLang('all')
    setVisibleCount(12)
  }

  const selectedMonthLabel = selectedMonth 
    ? monthGroups.find(m => m.key === selectedMonth)?.label 
    : null

  return (
    <section className="py-24 lg:py-32 bg-vibe-black min-h-screen">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="mb-16">
            <p className="section-label mb-4">Archive</p>
            <h2 className="text-3xl lg:text-4xl font-medium text-text-heading mb-4">
              All writing
            </h2>
            <p className="text-text-secondary text-base max-w-2xl leading-relaxed">
              Ideas on AI, vibe coding, product thinking, and building systems that amplify human capability.
            </p>
          </div>
        </ScrollReveal>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Left Sidebar - Timeline Navigation */}
          <ScrollReveal delay={0.1} className="lg:w-48 flex-shrink-0">
            <aside className="lg:sticky lg:top-28">
              <div className="flex items-center justify-between mb-6">
                <span className="text-tiny font-mono uppercase tracking-wider text-text-muted">
                  Timeline
                </span>
                {selectedMonth && (
                  <button
                    onClick={clearFilters}
                    className="text-tiny font-mono text-accent-primary hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
              
              <button
                onClick={() => { setSelectedMonth(null); setVisibleCount(12) }}
                className={`w-full text-left py-2 text-small font-mono transition-colors mb-4 border-l-2 pl-4 ${
                  !selectedMonth
                    ? 'border-accent-primary text-text-heading'
                    : 'border-transparent text-text-muted hover:text-text-secondary hover:border-vibe-border'
                }`}
              >
                All posts
                <span className="ml-2 text-text-muted">({blogPosts.length})</span>
              </button>

              <div className="space-y-6">
                {groupedByYear.map(([year, months]) => (
                  <div key={year}>
                    <div className="text-tiny font-mono uppercase tracking-wider text-text-secondary mb-3">
                      {year}
                    </div>
                    
                    <div className="space-y-1">
                      {months.map(month => (
                        <button
                          key={month.key}
                          onClick={() => { setSelectedMonth(month.key); setVisibleCount(12) }}
                          className={`w-full text-left py-1.5 text-small transition-colors border-l-2 pl-4 flex justify-between items-center ${
                            selectedMonth === month.key
                              ? 'border-accent-primary text-text-heading'
                              : 'border-transparent text-text-muted hover:text-text-secondary hover:border-vibe-border'
                          }`}
                        >
                          <span>{month.shortLabel}</span>
                          <span className="text-tiny opacity-50">{month.count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </aside>
          </ScrollReveal>

          {/* Right Content - Blog List */}
          <div className="flex-1 min-w-0">
            <ScrollReveal delay={0.15}>
              <div className="mb-8 space-y-4">
                {/* Filters Row */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center border border-vibe-border rounded-sm overflow-hidden">
                    <button
                      onClick={() => { setSelectedLang('all'); setVisibleCount(12) }}
                      className={`px-3 py-1.5 text-tiny font-mono transition-colors ${
                        selectedLang === 'all'
                          ? 'bg-accent-primary text-vibe-black'
                          : 'text-text-muted hover:text-text-primary'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => { setSelectedLang('en'); setVisibleCount(12) }}
                      className={`px-3 py-1.5 text-tiny font-mono border-l border-vibe-border transition-colors ${
                        selectedLang === 'en'
                          ? 'bg-accent-primary text-vibe-black'
                          : 'text-text-muted hover:text-text-primary'
                      }`}
                    >
                      EN
                    </button>
                    <button
                      onClick={() => { setSelectedLang('vi'); setVisibleCount(12) }}
                      className={`px-3 py-1.5 text-tiny font-mono border-l border-vibe-border transition-colors ${
                        selectedLang === 'vi'
                          ? 'bg-accent-primary text-vibe-black'
                          : 'text-text-muted hover:text-text-primary'
                      }`}
                    >
                      VI
                    </button>
                  </div>

                  <div className="flex-1 min-w-[180px] max-w-xs">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-transparent border border-vibe-border rounded-sm pl-9 pr-8 py-1.5 text-tiny text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-primary transition-colors"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {searchQuery && !searchIndex && (
                  <p role="status" className="text-tiny text-text-muted">
                    {searchError ? 'Full-text search is unavailable. Clear and re-enter your search to retry.' : 'Loading full-text search…'}
                  </p>
                )}

                {/* Hashtags */}
                {allHashtags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {allHashtags.slice(0, 6).map(tag => (
                      <button
                        key={tag}
                        onClick={() => setSelectedHashtag(selectedHashtag === tag ? null : tag)}
                        className="transition-colors hover:bg-card-muted"
                      >
                        <Badge variant={selectedHashtag === tag ? 'active' : 'default'}>
                          #{tag}
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}

                {/* Active Filter Indicator */}
                {selectedMonthLabel && (
                  <div className="flex items-center gap-2 text-small">
                    <span className="text-text-muted">Showing</span>
                    <span className="font-mono text-accent-primary">{filteredPosts.length}</span>
                    <span className="text-text-muted">posts from</span>
                    <span className="font-medium text-text-heading">{selectedMonthLabel}</span>
                  </div>
                )}
              </div>
            </ScrollReveal>

            {/* Blog Posts */}
            <div ref={ref} className="space-y-1">
              {visiblePosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: Math.min(index * 0.03, 0.2), duration: 0.3 }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="block py-5 border-b border-vibe-border hover:opacity-70 transition-opacity -mx-4 px-4"
                  >
                    <div className="flex items-start gap-4">
                      {/* Date Column */}
                      <div className="hidden sm:block w-24 flex-shrink-0 pt-0.5">
                        <span className="text-tiny font-mono text-text-muted">
                          {formatDate(post.date, 'en')}
                        </span>
                      </div>

                      {/* Content Column */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 sm:hidden">
                          <span className="text-tiny font-mono text-text-muted">
                            {formatDate(post.date, post.lang)}
                          </span>
                          <Badge variant="muted" className="text-[10px]">
                            {post.lang.toUpperCase()}
                          </Badge>
                        </div>

                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h2 className="text-base font-medium text-text-heading mb-1 line-clamp-2 leading-snug">
                              {getTitle(post)}
                            </h2>
                            <p className="text-text-secondary text-small line-clamp-2 leading-relaxed">
                              {getExcerpt(post)}
                            </p>
                          </div>

                          <div className="hidden sm:flex items-center gap-2 flex-shrink-0 pt-1">
                            <Badge variant="muted" className="text-[10px]">
                              {post.lang.toUpperCase()}
                            </Badge>
                            <ArrowRight className="w-4 h-4 text-text-muted" />
                          </div>
                        </div>

                        {post.hashtags.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-2">
                            <Hash className="w-3 h-3 text-text-muted" />
                            <span className="text-tiny text-text-muted">
                              {post.hashtags.slice(0, 3).join(' · ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            {filteredPosts.length === 0 && (!searchQuery || searchIndex || searchError) && (
              <div className="text-center py-16">
                <p className="text-text-muted mb-4">No posts found matching your filters.</p>
                <button
                  onClick={clearFilters}
                  className="text-accent-primary hover:underline text-small font-mono"
                >
                  Clear all filters
                </button>
              </div>
            )}

            {hasMore && (
              <div className="mt-10 text-center">
                <button
                  onClick={() => setVisibleCount(prev => prev + 12)}
                  className="btn-ghost px-6 py-2.5 text-tiny"
                >
                  Load more posts
                </button>
              </div>
            )}

            <ScrollReveal delay={0.2}>
              <div className="mt-16 pt-8 border-t border-vibe-border">
                <p className="text-text-muted text-tiny font-mono">
                  {blogPosts.length} thoughts from{' '}
                  <a 
                    href="https://t.me/will_thoughts" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-accent-primary hover:underline"
                  >
                    @will_thoughts
                  </a>
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  )
}
